// frontend/src/components/contacts/ContactList.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { formatPhone } from "../../utils/formatPhone";
import { toast } from "react-toastify";

const ContactList = ({ onEdit, onCreate }) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [contacts, setContacts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [relationshipFilter, setRelationshipFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  // Load branches for filter dropdown
  useEffect(() => {
    const loadBranches = async () => {
      if (!user?.tenant_id) return;
      try {
        const res = await secureAxios.get(`${apiBase}/api/branches`, {
          params: { tenant_id: user.tenant_id, is_active: true },
        });
        setBranches(res.data);
      } catch (err) {
        console.error("Error loading branches:", err);
      }
    };
    loadBranches();
  }, [apiBase, user?.tenant_id]);

  const loadContacts = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (!showInactive) {
        params.is_active = true;
      }

      if (relationshipFilter) {
        params.contact_status = relationshipFilter;
      }

      if (branchFilter) {
        params.branch_id = branchFilter;
      }

      const res = await secureAxios.get(`${apiBase}/api/employers`, { params });
      setContacts(res.data);
    } catch (err) {
      console.error("Error loading contacts:", err);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [user?.tenant_id, showInactive, relationshipFilter, branchFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDelete = async (contact) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${contact.company_name}"?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/employers/${contact.employer_id}`,
        {
          params: { tenant_id: user.tenant_id },
        }
      );
      toast.success("Contact deleted successfully");
      loadContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      toast.error(err.response?.data?.error || "Failed to delete contact");
    }
  };

  const formatAddress = (contact) => {
    const parts = [];
    if (contact.city) parts.push(contact.city);
    if (contact.state_code) parts.push(contact.state_code);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const getRelationshipBadge = (status) => {
    const badges = {
      prospect: { class: "bg-secondary", label: "Prospect" },
      contacted: { class: "bg-info", label: "Contacted" },
      interested: { class: "bg-warning text-dark", label: "Interested" },
      "active-partner": { class: "bg-success", label: "Active Partner" },
      inactive: { class: "bg-dark", label: "Inactive" },
    };
    const badge = badges[status] || badges.prospect;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="container my-4">
      {/* Search and Filters */}
      <div className="row g-2 mb-2 mb-md-3">
        <div className="col-12 col-md-3">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-6 col-md-2">
          <select
            className="form-select form-select-sm"
            value={relationshipFilter}
            onChange={(e) => setRelationshipFilter(e.target.value)}
            title="Filter by relationship status"
          >
            <option value="">All Status</option>
            <option value="prospect">Prospect</option>
            <option value="contacted">Contacted</option>
            <option value="interested">Interested</option>
            <option value="active-partner">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {/* Branch Filter */}
        <div className="col-6 col-md-2">
          <select
            className="form-select form-select-sm"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            title="Filter by branch/office"
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.branch_id} value={branch.branch_id}>
                {branch.branch_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6 col-md-2">
          <div className="form-check form-check-inline mt-1">
            <input
              type="checkbox"
              className="form-check-input"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="showInactive">
              <span className="d-none d-sm-inline">Show </span>Inactive
            </label>
          </div>
        </div>
        <div className="col-6 col-md-3 text-end">
          <button className="btn btn-primary btn-sm" onClick={onCreate}>
            <i className="bi bi-plus-circle me-1"></i>
            <span className="d-none d-sm-inline">Add </span>Contact
          </button>
        </div>
      </div>

      {/* Contacts Table/Cards */}
      {loading ? (
        <div className="text-center py-3 py-md-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-3 py-md-5">
          <i
            className="bi bi-building text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Contacts Found</h5>
          <p className="text-muted">
            {searchTerm || relationshipFilter || branchFilter
              ? "Try adjusting your search or filter criteria"
              : "Add your first employer contact to get started"}
          </p>
          {!searchTerm && !relationshipFilter && !branchFilter && (
            <button className="btn btn-primary" onClick={onCreate}>
              <i className="bi bi-plus-circle me-2"></i>
              Add First Contact
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-lg-block">
            <table className="table table-hover table-sm">
              <thead className="table-light">
                <tr>
                  <th>Company Name</th>
                  <th>Contact Person</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Branch/Office</th>
                  <th>Industry</th>
                  <th>Relationship</th>
                  <th>Last Contact</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.employer_id}
                    className={!contact.is_active ? "table-secondary" : ""}
                  >
                    <td>
                      <strong>{contact.company_name}</strong>
                      {contact.website && (
                        <a
                          href={
                            contact.website.startsWith("http")
                              ? contact.website
                              : `https://${contact.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2"
                          title="Visit website"
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                    </td>
                    <td>
                      {contact.contact_name || "-"}
                      {contact.contact_title && (
                        <small className="text-muted d-block">
                          {contact.contact_title}
                        </small>
                      )}
                    </td>
                    <td>
                      {contact.phone ? (
                        <a href={`tel:${contact.phone}`}>{formatPhone(contact.phone)}</a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{formatAddress(contact)}</td>
                    <td>
                      {contact.branch_name ? (
                        <span className="badge bg-light text-dark border">
                          <i className="bi bi-building me-1"></i>
                          {contact.branch_name}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{contact.industry || "-"}</td>
                    <td>{getRelationshipBadge(contact.contact_status || contact.relationship_status)}</td>
                    <td>
                      {contact.last_contact_date
                        ? new Date(contact.last_contact_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      {contact.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => onEdit(contact)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(contact)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-lg-none">
            {contacts.map((contact) => (
              <div 
                key={contact.employer_id} 
                className={`card mb-2 ${!contact.is_active ? "bg-light" : ""}`}
              >
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-bold text-truncate">
                        {contact.company_name}
                        {contact.website && (
                          <a
                            href={
                              contact.website.startsWith("http")
                                ? contact.website
                                : `https://${contact.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ms-2"
                          >
                            <i className="bi bi-box-arrow-up-right small"></i>
                          </a>
                        )}
                      </div>
                      <div className="small text-muted">
                        {contact.contact_name || "No contact"}
                        {contact.contact_title && ` - ${contact.contact_title}`}
                      </div>
                    </div>
                    <div className="ms-2 text-end">
                      {getRelationshipBadge(contact.contact_status || contact.relationship_status)}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 small text-muted mb-2">
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="text-decoration-none">
                        <i className="bi bi-telephone me-1"></i>
                        {formatPhone(contact.phone)}
                      </a>
                    )}
                    <span>
                      <i className="bi bi-geo-alt me-1"></i>
                      {formatAddress(contact)}
                    </span>
                    {contact.industry && (
                      <span>
                        <i className="bi bi-briefcase me-1"></i>
                        {contact.industry}
                      </span>
                    )}
                    {contact.branch_name && (
                      <span className="badge bg-light text-dark border">
                        <i className="bi bi-building me-1"></i>
                        {contact.branch_name}
                      </span>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {contact.last_contact_date ? (
                        <>
                          <i className="bi bi-clock me-1"></i>
                          {new Date(contact.last_contact_date).toLocaleDateString()}
                        </>
                      ) : (
                        "No contact date"
                      )}
                      {!contact.is_active && (
                        <span className="badge bg-secondary ms-2">Inactive</span>
                      )}
                    </small>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onEdit(contact)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(contact)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Summary */}
      {!loading && contacts.length > 0 && (
        <div className="text-muted small">
          Showing {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
          {branchFilter && branches.find(b => b.branch_id === branchFilter) && (
            <span> in {branches.find(b => b.branch_id === branchFilter).branch_name}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactList;